import { useEffect, useMemo, useRef, useState } from 'react';
import algoliasearch from 'algoliasearch';
import { getAlgoliaIndexNameForLang } from '../../../../util/urlHelpers';
import {
  getVisibleCategoryIds,
  getVisibleCategoryAlgoliaFilter,
} from '../../../../util/storeCategoryVisibility';
import {
  STORE_CATEGORY_HIERARCHICAL_ATTRIBUTES,
  HIERARCHICAL_SEPARATOR,
  buildHierarchicalTreeFromFacets,
} from './categoryFilterPipeline';

// Module-level client, mirroring AdditionalResults.js / StoreProfilePage.js — a
// read-only search key, safe on the client.
const ALGOLIA_APP_ID = process.env.REACT_APP_ALGOLIA_APP_ID;
const ALGOLIA_SEARCH_API_KEY = process.env.REACT_APP_ALGOLIA_SEARCH_API_KEY;
const searchClient =
  ALGOLIA_APP_ID && ALGOLIA_SEARCH_API_KEY
    ? algoliasearch(ALGOLIA_APP_ID, ALGOLIA_SEARCH_API_KEY)
    : null;

// The tree is keyword-independent, so one fetch per (store, locale, scope) is
// reused for the whole page session — including across every keystroke.
const treeCache = new Map();

/**
 * Fetch the full store category catalogue as a hierarchical menu tree, decoupled
 * from the live InstantSearch query.
 *
 * react-instantsearch's useHierarchicalMenu only returns facet values present in
 * the current result set, so a keyword search collapses the category strip to the
 * single matching branch. This hook runs a separate `search('', { facets })`
 * scoped to the store's published listings so the strip can keep showing every
 * category while the shopper types (RAS-2591).
 *
 * @param {Object} user - store owner user resource (has id + profile.publicData)
 * @param {string} locale - active locale, selects the Algolia index
 * @returns {{ fullFacetTree: Array|null }} tree, or null until the fetch resolves
 */
export const useStoreCategoryFacetTree = (user, locale) => {
  const ownerId = user?.id?.uuid || null;
  const publicData = user?.attributes?.profile?.publicData || {};
  const storeCategories = publicData.storeCategories || [];
  const storeCategoryGroups = publicData.storeCategoryGroups || {};
  const hasStoreCategories =
    Array.isArray(storeCategories) && storeCategories.length > 0;
  const showAllCategories = publicData.showAllCategories !== false;

  // Mirror StoreProfilePage's <Configure filters> so the facet scope matches the
  // results the shopper is actually browsing: when "Show All" is off, only
  // listings in visible configured categories are in scope.
  const filters = useMemo(() => {
    if (!ownerId) return null;
    let f = `user.id:${ownerId} AND state:published`;
    if (hasStoreCategories && !showAllCategories) {
      const { visibleCategoryIds } = getVisibleCategoryIds(
        storeCategories,
        storeCategoryGroups
      );
      const categoryFilter = getVisibleCategoryAlgoliaFilter(
        visibleCategoryIds,
        true
      );
      if (categoryFilter) {
        f = `${f} AND ${categoryFilter}`;
      }
    }
    return f;
    // storeCategories / storeCategoryGroups are stable within a store session.
  }, [ownerId, hasStoreCategories, showAllCategories, storeCategories, storeCategoryGroups]);

  const indexName = getAlgoliaIndexNameForLang(locale || 'en');
  const cacheKey = ownerId ? `${ownerId}::${indexName}::${filters}` : null;

  const [fullFacetTree, setFullFacetTree] = useState(() =>
    cacheKey && treeCache.has(cacheKey) ? treeCache.get(cacheKey) : null
  );
  const cacheKeyRef = useRef(cacheKey);

  useEffect(() => {
    cacheKeyRef.current = cacheKey;

    if (!cacheKey || !searchClient || !filters) {
      return undefined;
    }

    if (treeCache.has(cacheKey)) {
      setFullFacetTree(treeCache.get(cacheKey));
      return undefined;
    }

    let cancelled = false;
    setFullFacetTree(null);

    searchClient
      .initIndex(indexName)
      .search('', {
        filters,
        facets: STORE_CATEGORY_HIERARCHICAL_ATTRIBUTES,
        maxValuesPerFacet: 1000,
        hitsPerPage: 0,
        analytics: false,
      })
      .then(({ facets }) => {
        const tree = buildHierarchicalTreeFromFacets(
          facets,
          HIERARCHICAL_SEPARATOR
        );
        treeCache.set(cacheKey, tree);
        if (!cancelled && cacheKeyRef.current === cacheKey) {
          setFullFacetTree(tree);
        }
      })
      .catch(err => {
        // Non-fatal: consumers fall back to the live (query-scoped) menu.
        console.error('useStoreCategoryFacetTree: facet fetch failed', err);
        if (!cancelled && cacheKeyRef.current === cacheKey) {
          setFullFacetTree([]);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [cacheKey, filters, indexName]);

  return { fullFacetTree };
};

export default useStoreCategoryFacetTree;
